import api from './authService'
import { crearErrorAplicacionApi } from './apiError'

export interface Archivo {
  id: string
  nombre: string
  tipo: string
  tamaño: number
  fechaSubida: string
  fechaModificacion?: string
  usuario: string
  categoria?: string
  descripcion?: string
  estado: 'activo' | 'archivado' | 'eliminado'
  url?: string
  metadatos?: Record<string, any>
}

export interface FiltrosArchivos {
  busqueda?: string
  tipo?: string
  categoria?: string
  fechaInicio?: string
  fechaFin?: string
  usuario?: string
  estado?: string
}

export interface EstadisticasArchivos {
  total: number
  porTipo: Record<string, number>
  porCategoria: Record<string, number>
  tamañoTotal: number
  subidosHoy: number
  subidosEsteMes: number
}

export const archivosService = {
  /**
   * Obtener lista de archivos con filtros
   */
  async obtenerArchivos(filtros?: FiltrosArchivos): Promise<Archivo[]> {
    try {
      const params = new URLSearchParams()
      if (filtros?.busqueda) params.append('busqueda', filtros.busqueda)
      if (filtros?.tipo) params.append('tipo', filtros.tipo)
      if (filtros?.categoria) params.append('categoria', filtros.categoria)
      if (filtros?.fechaInicio) params.append('fecha_inicio', filtros.fechaInicio)
      if (filtros?.fechaFin) params.append('fecha_fin', filtros.fechaFin)
      if (filtros?.usuario) params.append('usuario', filtros.usuario)
      if (filtros?.estado) params.append('estado', filtros.estado)

      const response = await api.get(`/api/archivos?${params.toString()}`)
      return response.data.map((archivo: any) => ({
        id: String(archivo.id),
        nombre: archivo.nombre,
        tipo: archivo.tipo,
        tamaño: archivo.tamaño || archivo.size || 0,
        fechaSubida: archivo.fecha_subida || archivo.created_at,
        fechaModificacion: archivo.fecha_modificacion || archivo.updated_at,
        usuario: archivo.usuario || archivo.user_id,
        categoria: archivo.categoria,
        descripcion: archivo.descripcion,
        estado: archivo.estado || 'activo',
        url: archivo.url,
        metadatos: archivo.metadatos || archivo.metadata,
      }))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener archivos')
    }
  },

  /**
   * Obtener un archivo por ID
   */
  async obtenerArchivo(id: string): Promise<Archivo> {
    try {
      const response = await api.get(`/api/archivos/${id}`)
      const archivo = response.data
      return {
        id: String(archivo.id),
        nombre: archivo.nombre,
        tipo: archivo.tipo,
        tamaño: archivo.tamaño || archivo.size || 0,
        fechaSubida: archivo.fecha_subida || archivo.created_at,
        fechaModificacion: archivo.fecha_modificacion || archivo.updated_at,
        usuario: archivo.usuario || archivo.user_id,
        categoria: archivo.categoria,
        descripcion: archivo.descripcion,
        estado: archivo.estado || 'activo',
        url: archivo.url,
        metadatos: archivo.metadatos || archivo.metadata,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener archivo')
    }
  },

  /**
   * Subir un nuevo archivo
   */
  async subirArchivo(archivo: File, datos?: Partial<Archivo>): Promise<Archivo> {
    try {
      const formData = new FormData()
      formData.append('archivo', archivo)
      if (datos?.categoria) formData.append('categoria', datos.categoria)
      if (datos?.descripcion) formData.append('descripcion', datos.descripcion)

      const response = await api.post('/api/archivos', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const archivoSubido = response.data
      return {
        id: String(archivoSubido.id),
        nombre: archivoSubido.nombre,
        tipo: archivoSubido.tipo,
        tamaño: archivoSubido.tamaño || archivoSubido.size || 0,
        fechaSubida: archivoSubido.fecha_subida || archivoSubido.created_at,
        fechaModificacion: archivoSubido.fecha_modificacion || archivoSubido.updated_at,
        usuario: archivoSubido.usuario || archivoSubido.user_id,
        categoria: archivoSubido.categoria,
        descripcion: archivoSubido.descripcion,
        estado: archivoSubido.estado || 'activo',
        url: archivoSubido.url,
        metadatos: archivoSubido.metadatos || archivoSubido.metadata,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al subir archivo')
    }
  },

  /**
   * Actualizar un archivo
   */
  async actualizarArchivo(id: string, datos: Partial<Archivo>): Promise<Archivo> {
    try {
      const response = await api.put(`/api/archivos/${id}`, {
        nombre: datos.nombre,
        categoria: datos.categoria,
        descripcion: datos.descripcion,
        estado: datos.estado,
        metadatos: datos.metadatos,
      })

      const archivo = response.data
      return {
        id: String(archivo.id),
        nombre: archivo.nombre,
        tipo: archivo.tipo,
        tamaño: archivo.tamaño || archivo.size || 0,
        fechaSubida: archivo.fecha_subida || archivo.created_at,
        fechaModificacion: archivo.fecha_modificacion || archivo.updated_at,
        usuario: archivo.usuario || archivo.user_id,
        categoria: archivo.categoria,
        descripcion: archivo.descripcion,
        estado: archivo.estado || 'activo',
        url: archivo.url,
        metadatos: archivo.metadatos || archivo.metadata,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar archivo')
    }
  },

  /**
   * Eliminar un archivo
   */
  async eliminarArchivo(id: string): Promise<void> {
    try {
      await api.delete(`/api/archivos/${id}`)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al eliminar archivo')
    }
  },

  /**
   * Obtener estadísticas de archivos
   */
  async obtenerEstadisticas(): Promise<EstadisticasArchivos> {
    try {
      const response = await api.get('/api/archivos/estadisticas')
      const stats = response.data
      return {
        total: stats.total || 0,
        porTipo: stats.por_tipo || stats.by_type || {},
        porCategoria: stats.por_categoria || stats.by_category || {},
        tamañoTotal: stats.tamaño_total || stats.total_size || 0,
        subidosHoy: stats.subidos_hoy || stats.uploaded_today || 0,
        subidosEsteMes: stats.subidos_este_mes || stats.uploaded_this_month || 0,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener estadísticas')
    }
  },

  /**
   * Descargar un archivo
   */
  async descargarArchivo(id: string): Promise<Blob> {
    try {
      const response = await api.get(`/api/archivos/${id}/descargar`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al descargar archivo')
    }
  },
}
